// PWA service worker registration + push subscription helper
import { api, getClientId } from "./api";

const SW_PATH = "/service-worker.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH);
    navigator.serviceWorker.addEventListener("message", (e) => {
      if (e.data?.type === "navigate" && e.data.url) {
        try { window.history.pushState({}, "", e.data.url); window.dispatchEvent(new PopStateEvent("popstate")); } catch { /* ignore */ }
      }
    });
    return reg;
  } catch (e) {
    return null;
  }
}

export async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return { ok: false, reason: "unsupported" };
  if (typeof Notification === "undefined") return { ok: false, reason: "no-notification" };
  const reg = await navigator.serviceWorker.ready;
  let existing = await reg.pushManager.getSubscription();
  const { data } = await api.get("/push/vapid-public-key");
  const key = data.public_key;
  if (!key) return { ok: false, reason: "no-vapid" };
  if (!existing) {
    existing = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }
  const sub = existing.toJSON();
  await api.post("/push/subscribe", { endpoint: sub.endpoint, keys: sub.keys });
  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const j = sub.toJSON();
    try { await api.post("/push/unsubscribe", { endpoint: j.endpoint, keys: j.keys }); } catch { /* ignore */ }
    await sub.unsubscribe();
  }
}

export async function sendTestPush() {
  return api.post("/push/test").then((r) => r.data);
}

/* Returns the current PushSubscription if one exists, else null. */
export async function getActiveSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

/* Force-resyncs the given browser subscription with the backend under
   the current client_id. Idempotent (upsert by endpoint). */
export async function resyncSubscription(sub) {
  if (!sub) return false;
  try {
    const j = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
    return true;
  } catch { return false; }
}

export async function getPushStatus() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return { supported: false, subscribed: false };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      // Self-heal: if the browser has a subscription, make sure the backend
      // has it too under the CURRENT client_id. The /subscribe endpoint
      // is idempotent (upsert by endpoint). Fixes "toggle is ON but the
      // backend has no record for me" cases (e.g. localStorage cleared,
      // client_id rotated, or backend collection wiped).
      try {
        const j = sub.toJSON();
        await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
      } catch { /* offline or backend issue — keep optimistic */ }
    }
    return { supported: true, subscribed: !!sub };
  } catch {
    return { supported: true, subscribed: false };
  }
}
